import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from './api';

export interface Tier {
  code: string;
  cashbackRateBp: number;
  pointsMultiplierMilli: number;
  isPermanent: boolean;
}

export interface Vehicle {
  id: string;
  make: string;
  model: string;
  year: number | null;
  licensePlate: string | null;
}

export interface Order {
  reference: string;
  status: string;
  orderValueTetri: number;
  cashbackUsedTetri: number;
  cashbackEarnedTetri: number;
  pointsEarned: number;
  completedAt: string | null;
  vehicleId: string | null;
}

export interface Dashboard {
  customer: { firstName: string; name: string; loyaltyId: string; lastVisitAt: string | null };
  loyalty: {
    tier: Tier;
    cashbackAvailableTetri: number;
    cashbackPendingTetri: number;
    pointsBalance: number;
    lifetimeSpendTetri: number;
    totalOrders: number;
    nextTier: { code: string; remainingTetri: number; thresholdTetri: number } | null;
    isFinalTier: boolean;
  };
  vehicles: Vehicle[];
  recentOrders: Array<{
    reference: string;
    orderValueTetri: number;
    cashbackEarnedTetri: number;
    pointsEarned: number;
    completedAt: string | null;
    vehicleId: string | null;
  }>;
  unreadNotifications: number;
}

export interface Card {
  loyaltyId: string;
  name: string;
  tier: Tier;
  cashbackAvailableTetri: number;
  cashbackPendingTetri: number;
  pointsBalance: number;
  lifetimeSpendTetri: number;
  qr: { token: string; expiresInSeconds: number };
}

export interface Profile {
  id: string;
  loyaltyId: string;
  name: string;
  firstName: string;
  phone: string;
  email: string | null;
  preferredLanguage: 'ka' | 'ru' | 'en';
  otpOptedOut: boolean;
  memberSince: string;
}

export interface AuthMethods {
  otp: { available: boolean; preferred: boolean };
  accessLink: { available: boolean; hint: string };
}

export function useSession() {
  return useQuery<Profile | null>({
    queryKey: ['me'],
    retry: false,
    queryFn: async () => {
      try {
        return await api<Profile>('/me');
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
  });
}

export const useAuthMethods = () =>
  useQuery({
    queryKey: ['auth', 'methods'],
    staleTime: 5 * 60_000,
    queryFn: () => api<AuthMethods>('/auth/methods'),
  });

export const useDashboard = () =>
  useQuery({ queryKey: ['dashboard'], queryFn: () => api<Dashboard>('/dashboard') });

export const useCard = () =>
  useQuery({
    queryKey: ['card'],
    queryFn: () => api<Card>('/card'),
    // The QR token is short lived, so the card refreshes itself before it lapses.
    refetchInterval: 45_000,
    refetchIntervalInBackground: false,
  });

export interface LedgerEntry {
  id: string;
  type: string;
  description: string | null;
  createdAt: string;
}

export interface CashbackEntry extends LedgerEntry { amountTetri: number }
export interface PointsEntry extends LedgerEntry { amount: number }

export const useTransactions = () =>
  useQuery({
    queryKey: ['transactions'],
    queryFn: () => api<{ cashback: CashbackEntry[]; points: PointsEntry[] }>('/transactions'),
  });

export interface CustomerNotification {
  id: string;
  templateKey: string;
  params: Record<string, unknown> | null;
  createdAt: string;
  readAt: string | null;
}

export const useNotifications = () =>
  useQuery({
    queryKey: ['notifications'],
    queryFn: () => api<{ items: CustomerNotification[] }>('/notifications'),
  });

export function useMarkNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ ok: true }>('/notifications/read', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export type ReferralState =
  | { enabled: false }
  | {
    enabled: true;
    code: string | null;
    joined: number;
    rewarded: number;
    rewardCashbackTetri: number;
    rewardPoints: number;
  };

export const useReferral = () =>
  useQuery({
    queryKey: ['referral'],
    queryFn: () => api<ReferralState>('/referral'),
  });

export interface WalletState {
  available: { apple: boolean; google: boolean };
  passes: Array<{
    provider: 'APPLE' | 'GOOGLE';
    ready: boolean;
    status: string;
    /** Present once a Google card exists and can be installed. */
    saveUrl: string | null;
    issuedAt: string | null;
    lastSyncedAt: string | null;
  }>;
}

export const useWallet = () =>
  useQuery({
    queryKey: ['wallet'],
    queryFn: () => api<WalletState>('/wallet'),
    // Issuing happens in the background, so poll until the card is ready and
    // then stop. Without this the button stays on "preparing" forever.
    refetchInterval: (query) => {
      const state = query.state.data;
      if (!state) return false;
      return state.passes.some((pass) => !pass.ready) ? 3_000 : false;
    },
    // Keep polling while the phone is locked or the customer has switched away:
    // they left with "preparing" on screen and should not come back to it.
    refetchIntervalInBackground: true,
  });

export function useAddToWallet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (provider: 'APPLE' | 'GOOGLE') =>
      api<{ requested: boolean }>(`/wallet/${provider}`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['wallet'] }),
  });
}

export const useOrders = () =>
  useQuery({
    queryKey: ['orders'],
    queryFn: () => api<{ items: Order[] }>('/orders'),
  });

export const useVehicles = () =>
  useQuery({ queryKey: ['vehicles'], queryFn: () => api<{ items: Vehicle[] }>('/vehicles') });

export const useRequestOtp = () =>
  useMutation({
    mutationFn: (input: { phone: string; language: string }) =>
      api<{ sent: true }>('/auth/request-otp', { method: 'POST', body: input }),
  });

export function useVerifyOtp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { phone: string; code: string }) =>
      api<{ id: string; loyaltyId: string }>('/auth/verify-otp', { method: 'POST', body: input }),
    onSuccess: () => queryClient.invalidateQueries(),
  });
}

/**
 * Signing in from the card in the wallet.
 *
 * Separate from the staff-issued link because this one is reusable: the card
 * carries it for as long as it is on the phone, so opening it twice is normal
 * rather than a spent link.
 */
export function useWalletSignIn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (token: string) =>
      api<{ id: string; loyaltyId: string }>(`/auth/wallet/${token}`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries(),
  });
}

export function useRedeemAccessLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (token: string) =>
      api<{ id: string; loyaltyId: string }>(`/auth/access/${token}`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries(),
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ ok: true }>('/auth/logout', { method: 'POST' }),
    onSuccess: () => { queryClient.setQueryData(['me'], null); queryClient.clear(); },
  });
}

export function useOtpPreference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (optOut: boolean) =>
      api<{ ok: true }>(optOut ? '/auth/opt-out' : '/auth/opt-in', { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me'] }),
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<{ ok: true }>('/profile', { method: 'PATCH', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me'] }),
  });
}

export interface RewardOffer {
  id: string;
  name: Record<string, string>;
  description: Record<string, string> | null;
  pointsCost: number;
  validityDays: number | null;
  affordable: boolean;
  pointsShort: number;
  available: boolean;
  alreadyTaken: number;
}

export interface MyRedemption {
  id: string;
  code: string;
  status: string;
  pointsSpent: number;
  name: Record<string, string>;
  expiresAt: string | null;
  usedAt: string | null;
  createdAt: string;
}

export const useRewards = () =>
  useQuery({
    queryKey: ['rewards'],
    queryFn: () => api<{ enabled: boolean; pointsBalance: number; items: RewardOffer[] }>('/rewards'),
  });

export const useMyRedemptions = () =>
  useQuery({
    queryKey: ['rewards', 'mine'],
    queryFn: () => api<{ items: MyRedemption[] }>('/rewards/mine'),
  });

export function useRedeemReward() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ rewardId, key }: { rewardId: string; key: string }) =>
      api<{ code: string; name: Record<string, string>; pointsSpent: number; pointsBalance: number }>(
        `/rewards/${rewardId}/redeem`, { method: 'POST', idempotencyKey: key },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rewards'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['card'] });
    },
  });
}

/** Stages of the car that is in the shop right now. */
export interface ActiveStage {
  name: { ka: string; ru: string; en: string };
  workStatus: 'PENDING' | 'IN_PROGRESS' | 'DONE';
  startedAt: string | null;
  finishedAt: string | null;
}

export interface ActiveCar {
  reference: string;
  status: 'CONFIRMED' | 'IN_PROGRESS' | 'READY';
  acceptedAt: string | null;
  promisedAt: string | null;
  readyAt: string | null;
  make: string | null;
  model: string | null;
  licensePlate: string | null;
  stages: ActiveStage[];
}

export const useActiveWork = () =>
  useQuery({
    queryKey: ['active-work'],
    queryFn: () => api<{ items: ActiveCar[] }>('/active-work'),
    // The person is watching this while they wait, so it keeps itself fresh.
    refetchInterval: 60_000,
  });
